import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import BASE_URL from "../../../../config/config";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const EquiposManager = () => {
  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};
  const isAdmin = tokenInfo?.role === "admin";

  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const navigate = useNavigate();

  const TIPO_ESTADO = ["Activo", "En reparación", "No activo"];

  const [form, setForm] = useState({
    marca: "",
    modelo: "",
    numero_serie: "",
    cpu: "",
    ram: "",
    almacenamiento: "",
    sistemaOperativo: "",
    estado: "Activo", // coherente con el select
    personaAsignada: "",
    ubicacion: "",
    fechaCompra: "",
    proveedor: "",
    notas: "",
  });

  const [editId, setEditId] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: token || "",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const fetchEquipos = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BASE_URL}/equipos/`, { headers });
      setEquipos(data || []);
    } catch (err) {
      console.error("Error al obtener equipos:", err);
      toast.error("Error al obtener la lista de equipos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEquipos();
    // 
  }, []);

  const filtered = useMemo(() => {
    const q = searchText.toLowerCase();
    return equipos.filter((e) =>
      [
        e.marca,
        e.modelo,
        e.numero_serie,
        e.cpu,
        e.almacenamiento,
        e.sistemaOperativo,
        e.personaAsignada,
        e.ubicacion,
        e.proveedor,
        e.estado,
        e.notas,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [equipos, searchText]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pageItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setForm({
      marca: "",
      modelo: "",
      numero_serie: "",
      cpu: "",
      ram: "",
      almacenamiento: "",
      sistemaOperativo: "",
      estado: "Activo", // ⬅️ corregido (antes "activo")
      personaAsignada: "",
      ubicacion: "",
      fechaCompra: "",
      proveedor: "",
      notas: "",
    });
    setEditId(null);
    toast.info("Formulario reiniciado.");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      toast.error("Acceso restringido a administradores.");
      return;
    }

    try {
      if (editId) {
        await axios.put(`${BASE_URL}/equipos/${editId}`, form, { headers });
        toast.success("Equipo actualizado correctamente.");
      } else {
        await axios.post(`${BASE_URL}/equipos/`, form, { headers });
        toast.success("Equipo creado correctamente.");
      }
      await fetchEquipos();
      resetForm();
    } catch (err) {
      console.error("Error guardando equipo:", err);
      toast.error("Error guardando equipo. Revisa la consola.");
    }
  };

  const onEdit = (item) => {
    setEditId(item.id);
    setForm({
      marca: item.marca ?? "",
      modelo: item.modelo ?? "",
      numero_serie: item.numero_serie ?? "",
      cpu: item.cpu ?? "",
      ram: item.ram ?? "",
      almacenamiento: item.almacenamiento ?? "",
      sistemaOperativo: item.sistemaOperativo ?? "",
      estado: item.estado ?? "Activo",
      personaAsignada: item.personaAsignada ?? "",
      ubicacion: item.ubicacion ?? "",
      fechaCompra: item.fechaCompra ?? "",
      proveedor: item.proveedor ?? "",
      notas: item.notas ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info(`Editando equipo #${item.id}`);
  };

  const onDelete = async (id) => {
    if (!isAdmin) {
      toast.error("Acceso restringido a administradores.");
      return;
    }
    if (!window.confirm("¿Seguro que deseas eliminar este equipo?")) return;

    try {
      await axios.delete(`${BASE_URL}/equipos/${id}`, { headers });
      toast.success("Equipo eliminado correctamente.");
      await fetchEquipos();
    } catch (err) {
      console.error("Error al eliminar equipo:", err);
      toast.error("Error al eliminar equipo.");
    }
  };

  if (!token) {
    return (
      <div className="site-width">
        <p>Inicia sesión para continuar.</p>
        <ToastContainer position="bottom-right" />
      </div>
    );
  }

  return (
    <main className="site-width">
      <div className="dashboard-content gap-equipos">
      <button className="boton-volver" onClick={() => navigate('/dashboard')}>
        ← Volver
      </button>
        <h2>Gestión de equipos</h2>
        <p>Añade, edita, elimina y consulta periféricos asociados a equipos informáticos.</p>

        {/* Formulario de creación/edición */}
        <form className="form-equipos" onSubmit={onSubmit}>
          <h3>{editId ? "Editar equipo" : "Crear equipo"}</h3>
          <hr />
          <div className="grid-form-equipos">
            <div className="form-group">
              <span className="form-label">Marca</span>
              <input
                type="text"
                value={form.marca}
                onChange={(e) => setForm({ ...form, marca: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Modelo</span>
              <input
                type="text"
                value={form.modelo}
                onChange={(e) => setForm({ ...form, modelo: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Nº Serie</span>
              <input
                type="text"
                value={form.numero_serie}
                onChange={(e) => setForm({ ...form, numero_serie: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">CPU</span>
              <input
                type="text"
                value={form.cpu}
                onChange={(e) => setForm({ ...form, cpu: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">RAM (GB)</span>
              <input
                type="number"
                min="0"
                value={form.ram}
                onChange={(e) => setForm({ ...form, ram: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Almacenamiento</span>
              <input
                type="text"
                value={form.almacenamiento}
                onChange={(e) => setForm({ ...form, almacenamiento: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Sistema Operativo</span>
              <input
                type="text"
                value={form.sistemaOperativo}
                onChange={(e) =>
                  setForm({ ...form, sistemaOperativo: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <span className="form-label">Estado</span>
              <select
                value={form.estado}
                onChange={(e) => setForm({ ...form, estado: e.target.value })}
                required
              >
                {form.estado && !TIPO_ESTADO.includes(form.estado) && (
                  <option value={form.estado}>{form.estado} (actual)</option>
                )}
                {TIPO_ESTADO.map((estado) => (
                  <option key={estado} value={estado}>
                    {estado}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <span className="form-label">Fecha de compra</span>
              <input
                type="date"
                value={form.fechaCompra}
                onChange={(e) => setForm({ ...form, fechaCompra: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Persona asignada</span>
              <input
                type="text"
                value={form.personaAsignada}
                onChange={(e) => setForm({ ...form, personaAsignada: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Ubicación</span>
              <input
                type="text"
                value={form.ubicacion}
                onChange={(e) => setForm({ ...form, ubicacion: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Proveedor</span>
              <input
                type="text"
                value={form.proveedor}
                onChange={(e) => setForm({ ...form, proveedor: e.target.value })}
              />
            </div>

            <div className="form-group">
              <span className="form-label">Notas</span>
              <textarea
                value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {editId ? "Actualizar equipo" : "Añadir equipo"}
            </button>
            {editId && (
              <button
                type="button"
                className="btn-cancelar-edicion"
                onClick={resetForm}
              >
                Cancelar edición
              </button>
            )}
          </div>
        </form>

        {/* Filtros / búsqueda */}
        <div className="filtro-tickets-admin" style={{ marginTop: 24 }}>
          <input
            type="text"
            placeholder="Buscar por marca, modelo, nº serie, usuario, ubicación…"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Listado */}
        <div className="overflow-tabla" style={{ marginTop: 16 }}>
          {loading ? (
            <div className="loader-fullscreen"><p>Cargando…</p></div>
          ) : pageItems.length === 0 ? (
            <div className="no-items">Sin resultados</div>
          ) : (
            <div className="panel-tickets">
              <div className="equipos-list-header">
                <p>Marca</p>
                <p>Modelo</p>
                <p>Nº Serie</p>
                <p>CPU</p>
                <p>RAM</p>
                <p>SO</p>
                <p>Usuario</p>
                <p>Ubicación</p>
                <p>Estado</p>
                <p>Acciones</p>
              </div>

              <div className="equipos-list">
                {pageItems.map((e) => (
                  <div key={e.id} className="equipo-item">
                    <div className="nombre-item">{e.marca}</div>
                    <div className="nombre-item">{e.modelo}</div>
                    <div className="nombre-item">{e.numero_serie}</div>
                    <div className="nombre-item">{e.cpu}</div>
                    <div className="nombre-item">{e.ram}</div>
                    <div className="nombre-item">
                      {e.sistemaOperativo?.split(" ").slice(0, 3).join(" ")}
                      {e.sistemaOperativo?.split(" ").length > 3 ? "…" : ""}
                    </div>
                    <div className="nombre-item">{e.personaAsignada}</div>
                    <div className="nombre-item">{e.ubicacion}</div>
                    <div className="nombre-item">{e.estado}</div>
                    <div className="nombre-item actions">
                      <button className="btn-ghost" onClick={() => onEdit(e)}>
                        Editar
                      </button>
                      <button className="btn-danger" onClick={() => onDelete(e.id)}>
                        Eliminar
                      </button>
                      <Link to={`/dashboard/perifericos/${e.id}`}>
                        <button className="btn-ghost">Ver periféricos</button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Paginación */}
        {filtered.length > itemsPerPage && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span>
              Página {currentPage} de {totalPages || 1}
            </span>
            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Contenedor de toasts */}
      <ToastContainer position="bottom-right" />
    </main>
  );
};

export default EquiposManager;
